"use client"

import React, { useState } from "react"
import { Heart, MessageCircle, Send, Bookmark, MoreHorizontal } from "lucide-react"
import { cn } from "../../lib/utils"

export function InstagramPostCard({
  username,
  userAvatar,
  location,
  postImage,
  likes,
  caption,
  comments = 0,
  timeAgo,
  isVerified = false,
  className,
}) {
  const [isLiked, setIsLiked] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const [likesCount, setLikesCount] = useState(likes)

  const handleLike = () => {
    setIsLiked(!isLiked)
    setLikesCount(isLiked ? likesCount - 1 : likesCount + 1)
  }

  const formatLikes = (count) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`
    }
    return count.toLocaleString()
  }

  return (
    <div
      className={cn(
        "w-full max-w-[470px] bg-card border border-border rounded-lg overflow-hidden",
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5">
        <div className="flex items-center gap-3">
          <div className="relative w-8 h-8 rounded-full overflow-hidden ring-2 ring-pink-500 ring-offset-2 ring-offset-card">
            <img
              src={userAvatar || "/placeholder.svg"}
              alt={username}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex flex-col text-left">
            <div className="flex items-center gap-1">
              <span className="text-sm font-semibold text-foreground">
                {username}
              </span>
              {isVerified && (
                <svg
                  className="w-3.5 h-3.5 text-[#0095F6]"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
              )}
            </div>
            {location && (
              <span className="text-xs text-muted-foreground">{location}</span>
            )}
          </div>
        </div>
        <button className="p-2 hover:opacity-60 transition-opacity">
          <MoreHorizontal className="w-5 h-5 text-foreground" />
        </button>
      </div>

      {/* Post Image */}
      <div className="relative aspect-square w-full bg-muted">
        <img
          src={postImage || "/placeholder.svg"}
          alt="Post"
          className="w-full h-full object-cover"
          onDoubleClick={handleLike}
          onError={(e) => { e.target.onerror = null; e.target.src = '/img/logo1.png' }}
        />
      </div>

      {/* Actions */}
      <div className="px-3 py-2.5 text-left">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleLike}
              className="hover:opacity-60 transition-all active:scale-90"
            >
              <Heart
                className={cn(
                  "w-6 h-6 transition-colors",
                  isLiked ? "fill-red-500 text-red-500" : "text-foreground"
                )}
              />
            </button>
            <button className="hover:opacity-60 transition-opacity">
              <MessageCircle className="w-6 h-6 text-foreground" />
            </button>
            <button className="hover:opacity-60 transition-opacity">
              <Send className="w-6 h-6 text-foreground" />
            </button>
          </div>
          <button
            onClick={() => setIsSaved(!isSaved)}
            className="hover:opacity-60 transition-all active:scale-90"
          >
            <Bookmark
              className={cn(
                "w-6 h-6 transition-colors",
                isSaved ? "fill-foreground text-foreground" : "text-foreground"
              )}
            />
          </button>
        </div>

        {/* Likes */}
        <div className="mt-2.5">
          <span className="text-sm font-semibold text-foreground">
            {formatLikes(likesCount)} Me gusta
          </span>
        </div>

        {/* Caption */}
        <div className="mt-1">
          <p className="text-sm text-foreground whitespace-pre-line">
            <span className="font-semibold mr-1">{username}</span>
            {caption}
          </p>
        </div>

        {/* Comments */}
        {comments > 0 && (
          <button className="mt-1">
            <span className="text-sm text-muted-foreground">
              Ver los {comments} comentarios
            </span>
          </button>
        )}

        {/* Time */}
        <div className="mt-1.5">
          <span className="text-[10px] uppercase text-muted-foreground tracking-wide">
            {timeAgo}
          </span>
        </div>
      </div>

      {/* Add Comment */}
      <div className="flex items-center px-3 py-2.5 border-t border-border">
        <input
          type="text"
          placeholder="Agregar un comentario..."
          className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground text-foreground"
        />
        <button className="text-sm font-semibold text-[#0095F6] opacity-50 hover:opacity-100 transition-opacity">
          Publicar
        </button>
      </div>
    </div>
  );
}
